// src/pages/cadastros/Prestadores/NovoPrestador.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { db } from "../../../config/firebase";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";

import { obterProximoCodigoSequencialPrestador } from "./obterProximoCodigoSequencialPrestador";

function parseEspecialidades(texto) {
  const raw = (texto || "").trim();
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function NovoPrestador() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [nomeFantasia, setNomeFantasia] = useState("");
  const [razaoSocial, setRazaoSocial] = useState("");
  const [cnpj, setCnpj] = useState("");

  const [contatoNome, setContatoNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");

  const [especialidadesTexto, setEspecialidadesTexto] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);

  const inputStyle = {
    width: "100%",
    padding: "9px 11px",
    marginTop: 4,
    borderRadius: 8,
    border: "1px solid #334155",
    boxSizing: "border-box",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro("Usuário sem grupoId. Faça login novamente.");
      return;
    }

    if (!nomeFantasia.trim()) {
      setErro("Informe o Nome do Prestador.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || null;

    const empresaId = !modoGlobal && empresaAtual?.id ? empresaAtual.id : null;
    const empresaNome = !modoGlobal && empresaAtual?.nome ? empresaAtual.nome : null;

    setCarregando(true);

    try {
      const sequencial = await obterProximoCodigoSequencialPrestador(grupoId);
      const codigo = `PS${String(sequencial).padStart(4, "0")}`;

      const novaRef = doc(collection(db, "prestadores"));

      const especialidades = parseEspecialidades(especialidadesTexto);

      await setDoc(novaRef, {
        codigo,
        codigoSequencial: sequencial,

        nomeFantasia: nomeFantasia.trim(),
        razaoSocial: razaoSocial.trim() || null,
        cnpj: cnpj.trim() || null,

        contatoNome: contatoNome.trim() || null,
        telefone: telefone.trim() || null,
        email: email.trim() || null,

        especialidades,
        observacoes: observacoes.trim() || null,

        ativo: true,

        grupoId,
        grupoNome,
        empresaId,
        empresaNome,

        criadoPor: usuarioLogado?.uid || null,
        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSucesso(`Prestador criado com sucesso (código ${codigo}).`);

      setNomeFantasia("");
      setRazaoSocial("");
      setCnpj("");
      setContatoNome("");
      setTelefone("");
      setEmail("");
      setEspecialidadesTexto("");
      setObservacoes("");

      // volta para lista
      setTimeout(() => navigate("/cadastros/prestadores"), 350);
    } catch (e2) {
      console.error("Erro ao criar prestador:", e2);
      setErro(e2?.message || "Erro ao criar prestador.");
    } finally {
      setCarregando(false);
    }
  };

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div
        className={isMobile ? "m-card" : "pc-card"}
        style={{
          width: "100%",
          padding: isMobile ? 16 : "16px 40px",
          boxSizing: "border-box",
        }}
      >
        {!isMobile && (
          <header style={{ marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 22 }}>Novo Prestador de Serviço</h2>
            <p style={{ margin: "6px 0 0", opacity: 0.8 }}>
              Cadastro de empresas/contatos que realizam serviços externos (terceirizados).
            </p>
          </header>
        )}

        {erro && (
          <div
            style={{
              marginTop: isMobile ? 0 : 12,
              marginBottom: 12,
              padding: 8,
              borderRadius: 8,
              background: "#7f1d1d",
              color: "#fecaca",
              whiteSpace: "pre-wrap",
            }}
          >
            {erro}
          </div>
        )}

        {sucesso && (
          <div
            style={{
              marginTop: isMobile ? 0 : 12,
              marginBottom: 12,
              padding: 8,
              borderRadius: 8,
              background: "#064e3b",
              color: "#d1fae5",
              whiteSpace: "pre-wrap",
            }}
          >
            {sucesso}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{
            marginTop: isMobile ? 0 : 12,
            maxWidth: isMobile ? "100%" : 720,
            marginLeft: "auto",
            marginRight: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <label>
            <span style={{ fontSize: 14 }}>Nome Fantasia *</span>
            <input
              type="text"
              value={nomeFantasia}
              onChange={(e) => setNomeFantasia(e.target.value)}
              className={isMobile ? "m-input" : ""}
              style={{ ...inputStyle, color: "inherit" }}
              placeholder="Ex: Hidráulica do João"
            />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <label>
              <span style={{ fontSize: 14 }}>Razão Social</span>
              <input
                type="text"
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, color: "inherit" }}
                placeholder="Opcional"
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>CNPJ</span>
              <input
                type="text"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, color: "inherit" }}
                placeholder="Opcional"
              />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
            <label>
              <span style={{ fontSize: 14 }}>Contato</span>
              <input
                type="text"
                value={contatoNome}
                onChange={(e) => setContatoNome(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, color: "inherit" }}
                placeholder="Nome da pessoa"
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Telefone</span>
              <input
                type="text"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, color: "inherit" }}
                placeholder="(xx) xxxxx-xxxx"
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, color: "inherit" }}
                placeholder="Opcional"
              />
            </label>
          </div>

          <label>
            <span style={{ fontSize: 14 }}>Especialidades</span>
            <textarea
              value={especialidadesTexto}
              onChange={(e) => setEspecialidadesTexto(e.target.value)}
              className={isMobile ? "m-input" : ""}
              style={{ ...inputStyle, minHeight: 70, color: "inherit" }}
              placeholder="Separe por vírgula. Ex: Hidráulica, Elétrica, Usinagem"
            />
          </label>

          <label>
            <span style={{ fontSize: 14 }}>Observações</span>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              className={isMobile ? "m-input" : ""}
              style={{ ...inputStyle, minHeight: 80, color: "inherit" }}
              placeholder="Ex: atende emergências 24h..."
            />
          </label>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
            <button
              type="button"
              className="pc-btn-secundario"
              onClick={() => navigate("/cadastros/prestadores")}
              disabled={carregando}
            >
              Cancelar
            </button>

            <button type="submit" className="pc-btn-primario" disabled={carregando}>
              {carregando ? "Salvando..." : "Salvar prestador"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );

  return (
    <Layout
      tema={tema}
      titulo="Novo Prestador"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
    >
      {conteudo}
    </Layout>
  );
}

export default NovoPrestador;
